import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { ProjectMember, ProjectInvitation, ProjectMemberRole } from '@/types/project'

// ─── Fetch members ─────────────────────────────────────────
export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async (): Promise<ProjectMember[]> => {
      if (!projectId) return []
      const { data, error } = await supabase
        .from('project_members')
        .select('*')
        .eq('project_id', projectId)
        .order('added_at', { ascending: true })
      if (error) throw error
      return (data || []) as ProjectMember[]
    },
    enabled: !!projectId,
  })
}

// ─── Fetch pending invitations ─────────────────────────────
export function useProjectInvitations(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-invitations', projectId],
    queryFn: async (): Promise<ProjectInvitation[]> => {
      if (!projectId) return []
      const { data, error } = await supabase
        .from('project_invitations')
        .select('*')
        .eq('project_id', projectId)
        .eq('status', 'pending')
        .order('invited_at', { ascending: false })
      if (error) throw error
      return (data || []) as ProjectInvitation[]
    },
    enabled: !!projectId,
  })
}

// ─── Add member (existing user) ────────────────────────────
interface AddMemberData {
  projectId: string
  email: string
  role: ProjectMemberRole
  receiveEmails?: boolean
}

export function useAddProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ projectId, email, role, receiveEmails = true }: AddMemberData) => {
      // Check if user exists in auth
      const { data: existingMembers } = await supabase
        .from('project_members')
        .select('id')
        .eq('project_id', projectId)
        .eq('user_email', email)
        .maybeSingle()

      if (existingMembers) {
        throw new Error('Ce membre est deja ajoute au projet')
      }

      const { data, error } = await supabase
        .from('project_members')
        .insert({
          project_id: projectId,
          user_email: email,
          role,
          receive_emails: receiveEmails,
        })
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
    },
  })
}

// ─── Remove member ─────────────────────────────────────────
export function useRemoveProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ memberId, projectId }: { memberId: string; projectId: string }) => {
      const { error } = await supabase
        .from('project_members')
        .delete()
        .eq('id', memberId)

      if (error) throw error
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
    },
  })
}

// ─── Update member role / email prefs ──────────────────────
interface UpdateMemberData {
  memberId: string
  projectId: string
  role?: ProjectMemberRole
  receiveEmails?: boolean
}

export function useUpdateProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ memberId, role, receiveEmails }: UpdateMemberData) => {
      const updates: Record<string, unknown> = {}
      if (role !== undefined) updates.role = role
      if (receiveEmails !== undefined) updates.receive_emails = receiveEmails

      const { data, error } = await supabase
        .from('project_members')
        .update(updates)
        .eq('id', memberId)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
    },
  })
}

// ─── Create invitation ─────────────────────────────────────
interface InviteData {
  projectId: string
  email: string
  role: ProjectMemberRole
}

export function useCreateProjectInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ projectId, email, role }: InviteData) => {
      // Check if already a member
      const { data: existingMember } = await supabase
        .from('project_members')
        .select('id')
        .eq('project_id', projectId)
        .eq('user_email', email)
        .maybeSingle()

      if (existingMember) {
        throw new Error('Cet email est deja membre du projet')
      }

      // Check for pending invitation
      const { data: existingInvite } = await supabase
        .from('project_invitations')
        .select('id')
        .eq('project_id', projectId)
        .eq('email', email)
        .eq('status', 'pending')
        .maybeSingle()

      if (existingInvite) {
        throw new Error('Une invitation est deja en attente pour cet email')
      }

      const { data, error } = await supabase
        .from('project_invitations')
        .insert({
          project_id: projectId,
          email,
          role,
        })
        .select()
        .single()

      if (error) throw error
      return data as ProjectInvitation
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] })
    },
  })
}

// ─── Cancel invitation ─────────────────────────────────────
export function useCancelProjectInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ invitationId, projectId }: { invitationId: string; projectId: string }) => {
      const { error } = await supabase
        .from('project_invitations')
        .update({ status: 'cancelled' })
        .eq('id', invitationId)

      if (error) throw error
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] })
    },
  })
}

// ─── Invite member via API (adds + sends email) ───────────
const API_URL = import.meta.env.VITE_EMAIL_API_URL || 'https://api.lessablesweb.fr'

interface InviteMemberData {
  projectId: string
  email: string
  role: ProjectMemberRole
}

interface InviteMemberResponse {
  status: 'member_added' | 'invitation_sent'
  user_id?: string
  invitation_id?: string
  token?: string
}

export function useInviteProjectMember() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ projectId, email, role }: InviteMemberData): Promise<InviteMemberResponse> => {
      const { data: sessionData } = await supabase.auth.getSession()
      const session = sessionData?.session
      const token = session?.access_token

      const res = await fetch(`${API_URL}/api/project-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          project_id: projectId,
          email,
          role,
          admin_id: session?.user?.id || '',
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Erreur serveur' }))
        throw new Error(err.message || `Erreur ${res.status}`)
      }

      return res.json()
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['project-members', projectId] })
      queryClient.invalidateQueries({ queryKey: ['project-invitations', projectId] })
    },
  })
}

// ─── Get all project email recipients ──────────────────────
export function useProjectEmailRecipients(projectId: string | undefined) {
  return useQuery({
    queryKey: ['project-email-recipients', projectId],
    queryFn: async () => {
      if (!projectId) return []
      const { data, error } = await supabase
        .rpc('get_project_email_recipients', { p_project_id: projectId })

      if (error) throw error
      return data as Array<{ email: string; role: string; full_name: string }>
    },
    enabled: !!projectId,
  })
}
